import { useCallback, useMemo } from 'react';

import { InstanceOperationsName, OperationsName } from '@frontend/types';

import { useOpalUser } from './useOpalUser';

export function useOperations() {
  const { opalUser } = useOpalUser();

  const userOperations = useMemo(
    () => opalUser?.instance?.operations,
    [opalUser?.instance?.operations]
  );

  const instanceOperations = useMemo(
    () => opalUser?.instance?.product_capabilities,
    [opalUser?.instance?.product_capabilities]
  );

  /**
   * Function to check if user has at least one of the required operations.
   * @param operations - list of operations of which at least one is required (checked using Logical OR)
   * @returns boolean indicating if user has at least one of the required operations
   */
  const canUser = useCallback(
    (operations: OperationsName[]) => {
      return operations.some(operation => userOperations?.find(p => p.name === operation)?.allowed);
    },
    [userOperations]
  );

  /**
   * Function to check if instance has at least one of the required capabilities enabled.
   *
   * @param capabilities - list of capabilities of which at least one is required (checked using Logical OR)
   * @returns boolean indicating if instance has at least one of the required capabilities enabled
   */
  const canInstance = useCallback(
    (capabilities: InstanceOperationsName[]) => {
      return capabilities.some(
        capability => instanceOperations?.find(p => p.name === capability)?.allowed
      );
    },
    [instanceOperations]
  );

  return { operations: userOperations, instanceOperations, canUser, canInstance };
}
